import React, { useMemo, useRef, useState } from 'react';
import { Box, useTheme, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { usePlan } from '../../context/WorkPlanContext';
import { useProjects } from '../../context/ProjectContext';
import { useTeam } from '../../context/TeamContext';
import { useMember } from '../../context/MemberContext';

import { generateHashId } from '../../utils/helper';

// import AddProjectDialog from './AddProjectDialog';
import ProjectItem from './ProjectItem';
import { tokens } from '../../theme';
import MultipleSelect from '../../components/MultipleSelect';
import ProjectCalendarTimeline from '../../components/CalendarTimeline';

const ProjectListPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { projects } = useProjects();
  const { teamsExcludeSettings } = useTeam();
  const { plansByTeam, fetchPlansByTeam } = usePlan();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const calendarRef = useRef(null);
  const { members } = useMember();

  const teamResources = useMemo(() => {
    let teams = teamsExcludeSettings;
    if (selectedTeamIds.length > 0) {
      teams = teamsExcludeSettings.filter(({ id }) =>
        selectedTeamIds.includes(id)
      );
    }
    return teams.reduce(
      (obj, { id, name: title }) => ({ ...obj, [id]: { id, title } }),
      {}
    );
  }, [teamsExcludeSettings, selectedTeamIds]);

  const teamLoggedEvents = useMemo(() => {
    const search = searchQuery.toLowerCase();

    return plansByTeam.reduce((events, { team, data }) => {
      const { _id: teamId, name: teamName } = team;

      data.forEach(({ day, projects }) => {
        if (!projects.length) return;

        // Pre-filter projects based on searchQuery and teamId
        projects?.forEach(({ members, project, work_time_per_day }) => {
          if (!teamResources[teamId]) {
            return;
          }
          if (!teamResources[teamId].children) {
            teamResources[teamId].children = [];
          }
          const exist = teamResources[teamId].children.find(
            ({ id }) => id === project._id
          );
          if (!exist) {
            teamResources[teamId].children.push({
              id: project._id,
              title: project.name,
            });
          }

          // Convert project name to lowercase once
          const pNameLower = project?.name?.toLowerCase() || '';

          // Early return if project and team conditions are not met
          if (searchQuery && !pNameLower.includes(search)) {
            return;
          }

          // Check if any member matches the selectedMemberIds
          const filteredMembers =
            selectedMemberIds.length > 0
              ? members.filter((member) =>
                  selectedMemberIds.includes(member?._id)
                )
              : members;

          // Early return if no matching members are found
          if (selectedMemberIds.length > 0 && !filteredMembers.length) return;

          // Push the event only if all conditions are met
          events.push({
            id: generateHashId(),
            team: { id: teamId, name: teamName },
            title: project?.name || '',
            resourceId: project._id,
            start: day,
            end: day,
            project,
            members: filteredMembers,
            totalTimeLogged: work_time_per_day,
          });
        });
      });
      return events;
    }, []);
  }, [plansByTeam, searchQuery, selectedMemberIds, teamResources]);

  const filterBy = (key, arr, values) => {
    if (values.length === 0) return arr;

    return arr.filter((item) => {
      const id = item[key]?._id || item[key]?.id;
      return values.includes(id);
    });
  };

  const finalEvents = useMemo(() => {
    const byProject = filterBy('project', teamLoggedEvents, selectedProjectIds);
    return filterBy('team', byProject, selectedTeamIds);
  }, [teamLoggedEvents, selectedProjectIds, selectedTeamIds]);

  const handleTodayClick = () => {
    const calendarApi = calendarRef?.current?.getApi();
    setTimeout(() => calendarApi?.today(), 200);
  };

  const handleView = (project) => {
    navigate(`/project/${project._id}`);
  };

  const renderEventContent = (eventInfo) => {
    return (
      <ProjectItem
        eventInfo={eventInfo}
        selectedMember={selectedMemberIds}
        onViewDetail={() => handleView(eventInfo.event.extendedProps.project)}
      />
    );
  };

  const handleDatesSet = async ({ start_day, end_day }) => {
    try {
      await fetchPlansByTeam({
        start_day,
        end_day,
      });
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleOnChangeSearch = (e) => {
    setSearchQuery(e?.target?.value);
  };

  const filteredMembers = useMemo(() => {
    if (selectedTeamIds.length === 0) return members;
    const filterData = members.filter((it) =>
      selectedTeamIds.includes(it.team)
    );
    return filterData;
  }, [selectedTeamIds, members]);

  const renderSearchBox = () => {
    return (
      <Box
        display="flex"
        alignItems="flex-start"
        p={2}
        gap={2}
        flexWrap="wrap"
        flexDirection="column"
        sx={{ borderRadius: 1 }}>
        <TextField
          variant="outlined"
          placeholder="Search Projects"
          value={searchQuery}
          onChange={handleOnChangeSearch}
          size="small"
          sx={{
            width: 220,
            backgroundColor: colors.primary[400],
            borderRadius: 1,
          }}
        />
        <Box
          display="flex"
          flex={1}
          alignItems="center"
          gap={2}
          flexWrap="wrap">
          <MultipleSelect
            options={projects}
            selectedOption={selectedProjectIds}
            onChangeOptions={setSelectedProjectIds}
            allText="All project"
          />
          <MultipleSelect
            options={teamsExcludeSettings}
            selectedOption={selectedTeamIds}
            onChangeOptions={setSelectedTeamIds}
            allText="All team"
          />

          <MultipleSelect
            options={filteredMembers.map(({ id, full_name: name }) => ({
              id,
              name,
            }))}
            selectedOption={selectedMemberIds}
            onChangeOptions={setSelectedMemberIds}
            allText="All member"
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      {/* Search box & filter */}
      {renderSearchBox()}

      <Box p={2} pt={0.5}>
        <Box width="100%">
          <ProjectCalendarTimeline
            ref={calendarRef}
            resources={Object.values(teamResources)}
            events={finalEvents}
            adjustHeight={150}
            renderEventContent={renderEventContent}
            onChangeRangeDates={handleDatesSet}
            onTodayClick={handleTodayClick}
          />
        </Box>
      </Box>
    </>
  );
};

export default ProjectListPage;
